// Milepost strip — one block per loaded corridor, each split into aligned
// panels that share the milepost domain:
//   TOP:    intersections (icon by type; hover = the priorities-table figures)
//   MIDDLE: crashes along the corridor (severity-colored, stacked when close)
//   BOTTOM: the selected analysis' deficiency metric + run bands
// Clicking a crash, a run band, a deficiency bar or an intersection flies the
// map there and opens the matching popup (onFocus).
//
// Tooltip sync: the panels are synced by VALUE (`syncMethod="value"`), not by
// index — with index sync the crash panel (one point per crash) and the
// deficiency panel (one bin per 0.01 mi) would highlight unrelated mileposts.
// The crash panel's data IS the crash list, so its tooltip label is the
// crash's own milepost rather than the nearest baseline point.
import React, { useMemo, useState } from 'react';
import {
  ComposedChart, Bar, Scatter, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceArea, ResponsiveContainer,
} from 'recharts';
import { fmtUsd } from './insightsText';
import { siteKeyOf } from './cmfBenefit';

const AXIS_TICK = { fill: '#94a3b8', fontSize: 12 };
const TOOLTIP_STYLE = {
  backgroundColor: '#09101b',
  border: '1px solid #334155',
  borderRadius: '0.5rem',
  color: '#e2e8f0',
};

const METRIC_LABEL = {
  SSD: 'speed deficit (mph)',
  PSD: 'speed deficit (mph)',
  GLR: 'glare minutes / yr',
  FSA: 'cross slope (%)',
  HCA: 'curve safety score (%)',
  GRW: 'embankment height (ft)',
  ISD: 'required ISD (ft)',
  ALL: 'deficiency runs',
};
const ANALYSIS_RUN_COLORS = {
  SSD: '#c92a2a', PSD: '#9c36b5', GLR: '#e8a30c', FSA: '#1971c2', HCA: '#e8590c',
  GRW: '#5f3dc4', ISD: '#0ca678',
};
const CRASH_COLORS = { fatal: '#7e22ce', injury: '#e03131', pdo: '#868e96', unknown: '#adb5bd' };
const IX_GLYPH = { 'Four-way': '✚', 'T-intersection': '⊤', 'Y-intersection': 'Y', 'Five-point': '✳', Roundabout: '◯', Intersection: '◆' };

const fmtMp = (v) => (v === null || v === undefined ? '—' : Number(v).toFixed(3));

function CrashTip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const c = payload[0].payload.crash;
  if (!c) return null;
  return (
    <div className="safety-strip-tip">
      <div><b>MP {fmtMp(c.milepost)}</b> · {c.direction === 'P' ? 'EB' : 'WB'}{c.city ? ` · ${c.city}` : ''}</div>
      <div>{c.manner || 'Crash'} · {c.severity || c.severityClass}{c.isAnimal ? ' · animal' : ''}</div>
      <div className="safety-muted">{c.datetime ? c.datetime.slice(0, 16).replace('T', ' ') : ''}
        {Object.keys(c.findings || {}).length ? ` · findings: ${Object.keys(c.findings).join(', ')}` : ''}</div>
      <div className="safety-muted">click: open on the map · double-click: open the analysis report at this milepost</div>
    </div>
  );
}

function IntersectionTip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const ix = payload[0].payload.ix;
  if (!ix) return null;
  return (
    <div className="safety-strip-tip">
      <div><b>{IX_GLYPH[ix.type] || '◆'} {ix.type}</b> · Intersection {ix.id || '—'} · MP {fmtMp(ix.mp)}</div>
      {ix.isdFt ? <div>Required ISD {Math.round(ix.isdFt)} ft{ix.quality != null ? ` · quality ${(ix.quality * 100).toFixed(0)}%` : ''}</div> : null}
      <div>{ix.nonAnimal} crashes within 250 ft · K/A/B/C/O {ix.mix.K}/{ix.mix.A}/{ix.mix.B}/{ix.mix.C}/{ix.mix.O}</div>
      <div>Cost {fmtUsd(ix.cost)} · EPDO {ix.epdo}</div>
      <div className="safety-muted">click: open on the map{ix.reportUrl ? ' · double-click: open the ISD report' : ''}</div>
    </div>
  );
}

/** Report deep link for a crash: the finding of the selected analysis first,
 *  else any finding, else the run under it. */
function crashReportUrl(c, analysis, runs) {
  const f = c.findings || {};
  const pick = (analysis !== 'ALL' && f[analysis]?.reportUrl) ? f[analysis].reportUrl
    : Object.values(f).map((x) => x?.reportUrl).find(Boolean);
  if (pick) return pick;
  const run = (runs || []).find((r) => r.direction === c.direction && c.milepost >= r.mpStart - 0.1 && c.milepost <= r.mpEnd + 0.1);
  return run?.reportUrl || null;
}
const openReport = (url) => { if (url) window.open(url, '_blank', 'noopener'); };

function StripBlock({ block, filters, onFocus }) {
  const { analysisByDirection, crashes, runs, intersections, extent, road } = block;
  const [open, setOpen] = useState(true);
  const analysis = filters.analysis;
  const isAll = analysis === 'ALL';
  const domain = extent ? [Math.floor(extent[0]), Math.ceil(extent[1])] : ['auto', 'auto'];

  const data = useMemo(() => {
    if (isAll) {
      // ALL mode has no single metric: draw an invisible baseline so the axes
      // render; the runs' ReferenceAreas carry the content.
      return (extent || [0, 1]).map((mp) => ({ mp, value: 0 }));
    }
    const byMp = new Map();
    for (const [dir, payload] of Object.entries(analysisByDirection || {})) {
      if (!payload) continue;
      if (filters.direction !== 'both' && dir !== filters.direction) continue;
      for (const bin of payload.strip?.bins || []) {
        if (filters.mpRange && (bin.mp < filters.mpRange[0] || bin.mp > filters.mpRange[1])) continue;
        const prev = byMp.get(bin.mp);
        // keep the worse value across directions (negative-is-worse for
        // SSD/PSD/FSA cross slope; GLR positive-is-worse; HCA lower-is-worse)
        const worse = prev === undefined
          ? bin.value
          : analysis === 'GLR' ? Math.max(prev, bin.value) : Math.min(prev, bin.value);
        byMp.set(bin.mp, worse);
      }
    }
    return [...byMp.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([mp, value]) => ({
        mp,
        value: analysis === 'GLR' || analysis === 'HCA' ? value : Math.abs(value),
        raw: value,
      }));
  }, [analysisByDirection, filters, analysis, isAll, extent]);

  // crash panel: the data IS the crash list (sorted), stacked when close
  const crashPoints = useMemo(() => {
    const seen = {};
    return [...(crashes || [])]
      .sort((a, b) => a.milepost - b.milepost)
      .map((c) => {
        const bucket = Math.round(c.milepost * 12);      // ~0.083 mi
        const level = (seen[bucket] = (seen[bucket] || 0) + 1);
        return { mp: c.milepost, y: level, crash: c };
      });
  }, [crashes]);
  const maxStack = Math.max(3, ...crashPoints.map((p) => p.y));

  const ixPoints = useMemo(() => (intersections || [])
    .filter((ix) => ix.mp != null)
    .sort((a, b) => a.mp - b.mp)
    .map((ix) => ({ mp: ix.mp, y: 1, ix })), [intersections]);

  const focusRun = (run) => onFocus && onFocus({
    bounds: (run.coords || []).filter((p) => p && p[0] != null),
    lat: run.coords?.[0]?.[0], lng: run.coords?.[0]?.[1],
    runKey: siteKeyOf(run), key: `strip-run-${siteKeyOf(run)}-${Date.now()}`,
  });
  // deficiency bar: the run containing this milepost, else the nearest one
  const runFor = (mp) => (mp == null ? null
    : (runs || []).find((r) => mp >= r.mpStart - 0.005 && mp <= r.mpEnd + 0.005)
      || [...(runs || [])].sort((a, b) => Math.abs(a.mpStart - mp) - Math.abs(b.mpStart - mp))[0]);
  const focusMp = (mp) => { const run = runFor(mp); if (run) focusRun(run); };

  const analysisColor = ANALYSIS_RUN_COLORS[analysis] || '#e8590c';
  const runAreas = (runs || []).map((run) => (
    <ReferenceArea key={siteKeyOf(run)}
      x1={run.mpStart} x2={Math.max(run.mpEnd, run.mpStart + 0.01)}
      fill={isAll ? (ANALYSIS_RUN_COLORS[run.analysis] || '#c92a2a') : analysisColor}
      fillOpacity={isAll ? 0.28 : 0.14}
      className="safety-strip-run" onClick={() => focusRun(run)} onDoubleClick={() => openReport(run.reportUrl)} />
  ));


  const header = (
    <button type="button" className="safety-strip-road safety-strip-toggle" onClick={() => setOpen(!open)} aria-expanded={open}>
      <span className={`safety-section-chevron ${open ? 'open' : ''}`}>▸</span>
      {road || block.key}{extent ? ` · MP ${extent[0]}–${extent[1]}` : ''}
      <span className="safety-muted safety-strip-meta">
        {crashPoints.length} crashes · {(runs || []).length} runs{ixPoints.length ? ` · ${ixPoints.length} intersections` : ''}
      </span>
    </button>
  );
  if (!open) return <div className="safety-strip-split">{header}</div>;
  if (!data.length && !crashPoints.length) {
    return <div className="safety-strip-split">{header}<div className="safety-strip-empty">No strip data for {road || 'this corridor'}.</div></div>;
  }
  return (
    <div className="safety-strip-split">
      {header}
      {ixPoints.length > 0 && (
        <div>
          <div className="safety-strip-label">Intersections</div>
          <ResponsiveContainer width="100%" height={44}>
            <ComposedChart data={ixPoints} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
              <XAxis dataKey="mp" type="number" domain={domain} hide />
              <YAxis domain={[0, 2]} width={44} tick={false} axisLine={false}
                label={{ value: 'ISD', angle: -90, position: 'insideLeft', fill: '#64748b', fontSize: 10 }} />
              <Tooltip content={<IntersectionTip />} cursor={{ stroke: '#334155' }} wrapperStyle={{ zIndex: 5 }} />
              <Scatter name="intersection" data={ixPoints} dataKey="y" isAnimationActive={false}
                onClick={(p) => { const ix = p?.ix || p?.payload?.ix; if (ix && onFocus) onFocus({ lat: ix.lat, lng: ix.lng, zoom: 16, intersectionId: ix.id, key: `strip-ix-${ix.id}-${Date.now()}` }); }}
                shape={(props) => (
                  <text x={props.cx} y={props.cy + 5} textAnchor="middle" fontSize={14}
                    fill={props.payload.ix.mix.K ? '#e03131' : '#0ca678'} style={{ cursor: 'pointer' }}
                    onDoubleClick={(e) => { e.stopPropagation(); openReport(props.payload.ix.reportUrl); }}>
                    {IX_GLYPH[props.payload.ix.type] || '◆'}
                  </text>
                )} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
      {crashPoints.length > 0 && (
        <div>
          <div className="safety-strip-label">Crashes</div>
          <ResponsiveContainer width="100%" height={90}>
            <ComposedChart data={crashPoints}
              margin={{ top: 6, right: 16, bottom: 0, left: 0 }}
              syncId={`safety-strip-${block.key}`} syncMethod="value">
              <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="mp" type="number" domain={domain} hide />
              <YAxis domain={[0, maxStack + 1]} width={44} tick={false}
                label={{ value: 'crashes', angle: -90, position: 'insideLeft', fill: '#64748b', fontSize: 10 }} />
              <Tooltip content={<CrashTip />} cursor={{ stroke: '#334155' }} wrapperStyle={{ zIndex: 5 }} />
              <Scatter name="crash" data={crashPoints} dataKey="y" isAnimationActive={false}
                onClick={(p) => {
                  const c = p?.crash || p?.payload?.crash;
                  if (c && onFocus) onFocus({ lat: c.lat, lng: c.lon, zoom: 16, crashId: c.id, key: `strip-crash-${c.id}-${Date.now()}` });
                }}
                shape={(props) => (
                  <circle cx={props.cx} cy={props.cy} r={3.6} style={{ cursor: 'pointer' }}
                    onDoubleClick={(e) => { e.stopPropagation(); openReport(crashReportUrl(props.payload.crash, analysis, runs)); }}
                    fill={props.payload.crash.isAnimal ? 'none' : CRASH_COLORS[props.payload.crash.severityClass]}
                    stroke={props.payload.crash.isAnimal ? '#9aa7ac' : 'none'}
                    strokeWidth={props.payload.crash.isAnimal ? 1.4 : 0} />
                )} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
      <div>
        <div className="safety-strip-label">
          {isAll ? 'Deficiency runs (colored by analysis) — click: open on the map · double-click: open the report' : `${analysis} deficiencies — click: open on the map · double-click: open the report at this milepost`}
        </div>
        <ResponsiveContainer width="100%" height={190}>
          <ComposedChart data={data} margin={{ top: 4, right: 16, bottom: 4, left: 0 }}
            syncId={`safety-strip-${block.key}`} syncMethod="value">
            <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
            <XAxis dataKey="mp" type="number" domain={domain} tick={AXIS_TICK}
              label={{ value: 'Milepost', position: 'insideBottomRight', offset: -2, fill: '#64748b', fontSize: 11 }} />
            <YAxis tick={AXIS_TICK} width={44}
              label={{ value: METRIC_LABEL[analysis], angle: -90, position: 'insideLeft', fill: '#64748b', fontSize: 11 }} />
            <Tooltip
              contentStyle={TOOLTIP_STYLE}
              formatter={(v) => [v, METRIC_LABEL[analysis]]}
              labelFormatter={(mp) => `MP ${Number(mp).toFixed(2)}`}
            />
            {runAreas}
            {!isAll && (
              <Bar dataKey="value" fill={analysisColor} barSize={2} isAnimationActive={false}
                onClick={(d) => { const mp = d?.payload?.mp ?? d?.mp; if (mp != null) focusMp(mp); }}
                onDoubleClick={(d) => { const mp = d?.payload?.mp ?? d?.mp; const run = runFor(mp); openReport(run?.reportUrl); }}
                style={{ cursor: 'pointer' }} />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function StripChart({ blocks, filters, onFocus }) {
  if (!blocks?.length) return <div className="safety-strip-empty">No strip data for this selection.</div>;
  return (
    <div className="safety-strip-blocks">
      {blocks.map((b) => (
        <StripBlock key={b.key} block={b} filters={filters} onFocus={onFocus} />
      ))}
    </div>
  );
}
